import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import RebarSlabModulePanel from '../../components/RebarSlabModulePanel'
import RebarBeamModulePanel from '../../components/RebarBeamModulePanel'
import RebarColumnModulePanel from '../../components/RebarColumnModulePanel'
import RebarFoundationModulePanel from '../../components/RebarFoundationModulePanel'
import RebarWallModulePanel from '../../components/RebarWallModulePanel'
import SurfaceAreaModulePanel from '../../components/SurfaceAreaModulePanel'
import ConcreteVolumeModulePanel from '../../components/ConcreteVolumeModulePanel'
import FormworkModulePanel from '../../components/FormworkModulePanel'
import ExcavationModulePanel from '../../components/ExcavationModulePanel'
import MepEquipmentModulePanel from '../../components/MepEquipmentModulePanel'
import PipeworkModulePanel from '../../components/PipeworkModulePanel'
import ContainmentModulePanel from '../../components/ContainmentModulePanel'
import DuctworkModulePanel from '../../components/DuctworkModulePanel'
import CablesModulePanel from '../../components/CablesModulePanel'
import DisposalModulePanel from '../../components/DisposalModulePanel'
import CompactionModulePanel from '../../components/CompactionModulePanel'
import SheetMetalModulePanel from '../../components/SheetMetalModulePanel'
import DoorsWindowsModulePanel from '../../components/DoorsWindowsModulePanel'
import FacadeModulePanel from '../../components/FacadeModulePanel'
import MiscellaneousModulePanel from '../../components/MiscellaneousModulePanel'
import ModulePickerPopover from '../../components/ModulePickerPopover'
import DedicatedModuleWrapper from '../../components/DedicatedModuleWrapper'
import ModuleBreakdownRenderer from '../../components/ModuleBreakdownRenderer'
import { MODULE_REGISTRY } from '../../lib/quantityEngine'

// Centralized dispatch map: moduleKey -> dedicated panel component.
// Adding a future dedicated module requires ONLY a new entry here (and
// a MODULE_REGISTRY registration) — never another manual ternary chain.
const DEDICATED_PANEL_COMPONENTS = {
  rebar_slab: RebarSlabModulePanel,
  rebar_beam: RebarBeamModulePanel,
  rebar_column: RebarColumnModulePanel,
  rebar_foundation: RebarFoundationModulePanel,
  rebar_wall: RebarWallModulePanel,
  surface_area: SurfaceAreaModulePanel,
  concrete_volume: ConcreteVolumeModulePanel,
  formwork: FormworkModulePanel,
  excavation: ExcavationModulePanel,
  mep_equipment: MepEquipmentModulePanel,
  pipework: PipeworkModulePanel,
  containment: ContainmentModulePanel,
  ductwork: DuctworkModulePanel,
  cables: CablesModulePanel,
  disposal: DisposalModulePanel,
  compaction: CompactionModulePanel,
  sheet_metal: SheetMetalModulePanel,
  doors_windows: DoorsWindowsModulePanel,
  facade: FacadeModulePanel,
  miscellaneous: MiscellaneousModulePanel,
}

// =======================================================================
// QuantificationPage — Phase A.5. The generic Resource Quantification
// workflow, deliberately independent from BOQ Item and BOQ Quantity.
//
// STRICT RULES:
//   - A Scope never requires a BOQ Item to exist.
//   - Outputs never write to boq_item.resolved_quantity — that remains
//     the exclusive responsibility of quantity_source /
//     recapture_quantity_source(), completely untouched here.
//   - Rate Build-Up linkage is READ-ONLY on this page (status display
//     only) — the primary linking workflow lives in Rate Build-Up
//     itself, per the approved decision.
//   - geometry_snapshot always uses the generic { items: [...] } shape
//     defined in this Phase — no module-specific fields.
// =======================================================================

// The ONLY module registered here is a deliberately generic placeholder,
// proving the full workflow end-to-end without any engineering-specific
// (e.g. Rebar) assumption. Its `tab` key ('calculated') is a local
// concept only — it intentionally does NOT exist as a key in
// MODULE_REGISTRY, so `MODULE_REGISTRY['calculated']?.isDedicatedPanel`
// safely evaluates to falsy and the generic bottom-level Save Output
// button correctly remains available for it.
const PLACEHOLDER_MODULE = {
  moduleKey: 'generic_area_x_factor',
  label: 'Generic — Area × Factor (placeholder, for workflow validation)',
  inputFields: [
    { key: 'factor', label: 'Factor', type: 'number', required: true },
  ],
  calculate(items, inputs) {
    const totalArea = items.reduce((sum, it) => sum + Number(it.value), 0)
    const factor = Number(inputs.factor)
    if (items.length === 0) throw new Error('At least one Geometry item must be selected.')
    if (inputs.factor === '' || inputs.factor == null || Number.isNaN(factor)) throw new Error('Factor is required.')
    return {
      steps: [
        { label: 'Total Geometry Value', value: totalArea, unit: items[0]?.unit || '' },
        { label: 'Factor (input)', value: factor, unit: '' },
      ],
      finalQuantity: totalArea * factor,
    }
  },
}

export default function QuantificationPage() {
  const { tenderId, revisionId } = useParams()
  const [scopes, setScopes] = useState(null)
  const [selectedScopeId, setSelectedScopeId] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [newScopeName, setNewScopeName] = useState('')
  const [revisionStatus, setRevisionStatus] = useState(null)

  const loadScopes = useCallback(async () => {
    const [{ data, error: err }, { data: revData }] = await Promise.all([
      supabase.from('quantification_scope').select('id, name, created_at').eq('estimate_revision_id', revisionId).order('created_at'),
      supabase.from('estimate_revision').select('status').eq('id', revisionId).single(),
    ])
    if (err) return setError(err.message)
    setScopes(data)
    setRevisionStatus(revData?.status ?? null)
    if (!selectedScopeId && data.length > 0) setSelectedScopeId(data[0].id)
  }, [revisionId, selectedScopeId])

  useEffect(() => { loadScopes() }, [loadScopes])

  const isFrozen = revisionStatus === 'frozen'

  async function handleCreateScope() {
    if (!newScopeName.trim()) return
    const { data: authData } = await supabase.auth.getUser()
    const { error: err } = await supabase.from('quantification_scope').insert({
      estimate_revision_id: revisionId, name: newScopeName.trim(), created_by: authData.user?.id,
    })
    if (err) setError(err.message)
    else { setSuccessMsg(`Scope "${newScopeName.trim()}" created.`); setNewScopeName(''); await loadScopes() }
  }

  if (scopes === null) return <SkeletonRows rows={4} cols={2} />

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>Quantification</h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Resource Quantity workspace — independent from BOQ. A Scope groups shared geometry and produces one or more
        independent Resource Quantity Outputs (e.g. Concrete, Reinforcement, Formwork), each with its own unit and calculation.
      </p>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {isFrozen && (
        <div style={{ padding: '10px 14px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          🔒 This revision is frozen — quantification data is read-only.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'flex-start' }}>
        {/* ===================== LEFT: Scope list ===================== */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 8 }}>Scopes</div>
          {scopes.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>No scopes yet.</div>}
          {scopes.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedScopeId(s.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6, marginBottom: 4,
                background: selectedScopeId === s.id ? '#F3F4F6' : 'transparent', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: selectedScopeId === s.id ? 700 : 500, color: colors.textPrimary,
              }}
            >
              {s.name}
            </button>
          ))}
          {!isFrozen && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
              <Input placeholder="New scope name…" value={newScopeName} onChange={(e) => setNewScopeName(e.target.value)} style={{ width: '100%', fontSize: 12, marginBottom: 6 }} />
              <Button size="sm" onClick={handleCreateScope} disabled={!newScopeName.trim()} style={{ width: '100%' }}>+ New Scope</Button>
            </div>
          )}
        </div>

        {/* ===================== RIGHT: Scope detail ===================== */}
        <div>
          {!selectedScopeId ? (
            <EmptyState title="Select or create a Scope" description="A Scope groups shared geometry and produces independent resource quantities." />
          ) : (
            <ScopeDetail
              key={selectedScopeId}
              scopeId={selectedScopeId}
              tenderId={tenderId}
              revisionId={revisionId}
              isFrozen={isFrozen}
              onError={setError}
              onSuccess={setSuccessMsg}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------
function ScopeDetail({ scopeId, tenderId, revisionId, isFrozen, onError, onSuccess }) {
  const [linkedItems, setLinkedItems] = useState(null)
  const [allTakeoffItems, setAllTakeoffItems] = useState(null)
  const [outputs, setOutputs] = useState(null)
  const [showLinkGeometry, setShowLinkGeometry] = useState(false)
  const [showAddOutput, setShowAddOutput] = useState(false)
  const [recalcTarget, setRecalcTarget] = useState(null) // output being recalculated, or null

  const load = useCallback(async () => {
    const [{ data: links, error: e1 }, { data: allItems, error: e2 }, { data: outs, error: e3 }] = await Promise.all([
      supabase.from('scope_geometry_link').select('takeoff_item_id, takeoff_item(id, description, unit, measurement_type)').eq('scope_id', scopeId),
      supabase.from('takeoff_item').select('id, description, unit, measurement_type').eq('tender_id', tenderId),
      supabase.from('resource_quantity_output').select('*').eq('scope_id', scopeId).eq('is_active', true).order('created_at'),
    ])
    if (e1) return onError(e1.message)
    if (e2) return onError(e2.message)
    if (e3) return onError(e3.message)
    setLinkedItems(links.map((l) => l.takeoff_item))
    setAllTakeoffItems(allItems)
    setOutputs(outs)
  }, [scopeId, tenderId, onError])

  useEffect(() => { load() }, [load])

  async function handleLinkGeometry(takeoffItemId) {
    const { error } = await supabase.from('scope_geometry_link').insert({ scope_id: scopeId, takeoff_item_id: takeoffItemId })
    if (error) onError(error.message)
    else await load()
  }
  async function handleUnlinkGeometry(takeoffItemId) {
    const { error } = await supabase.from('scope_geometry_link').delete().eq('scope_id', scopeId).eq('takeoff_item_id', takeoffItemId)
    if (error) onError(error.message)
    else await load()
  }

  const unlinkedItems = (allTakeoffItems || []).filter((t) => !(linkedItems || []).some((l) => l.id === t.id))

  if (linkedItems === null) return <SkeletonRows rows={3} cols={2} />

  return (
    <div>
      {/* ===================== GEOMETRY ===================== */}
      <SectionCard title="Geometry" action={!isFrozen && <Button size="sm" variant="secondary" onClick={() => setShowLinkGeometry(true)}>+ Link Geometry</Button>}>
        {linkedItems.length === 0 ? (
          <div style={{ fontSize: 12, color: colors.textMuted }}>No geometry linked yet.</div>
        ) : (
          linkedItems.map((item) => (
            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
              <span>{item.description} <span style={{ color: colors.textMuted }}>({item.measurement_type}, {item.unit})</span></span>
              {!isFrozen && <button onClick={() => handleUnlinkGeometry(item.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 11 }}>Unlink</button>}
            </div>
          ))
        )}
        {showLinkGeometry && (
          <div style={{ marginTop: 10, padding: 10, border: `1px solid ${colors.border}`, borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>Available Takeoff Items</div>
            {unlinkedItems.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted }}>No more items to link.</div>}
            {unlinkedItems.map((item) => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12 }}>
                <span>{item.description} ({item.measurement_type}, {item.unit})</span>
                <button onClick={() => handleLinkGeometry(item.id)} style={{ background: 'none', border: `1px solid ${colors.primary}`, color: colors.primary, borderRadius: 4, cursor: 'pointer', fontSize: 11, padding: '1px 8px' }}>Link</button>
              </div>
            ))}
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <Button size="sm" variant="secondary" onClick={() => setShowLinkGeometry(false)}>Close</Button>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ===================== OUTPUTS ===================== */}
      <SectionCard title="Resource Quantity Outputs" action={!isFrozen && <Button size="sm" onClick={() => setShowAddOutput(true)}>+ Add Output</Button>} style={{ marginTop: 16 }}>
        {outputs === null ? <SkeletonRows rows={2} cols={1} /> : outputs.length === 0 ? (
          <div style={{ fontSize: 12, color: colors.textMuted }}>No outputs yet. This Scope produces no resource quantities until one is added.</div>
        ) : (
          outputs.map((output) => (
            <OutputRow key={output.id} output={output} scopeId={scopeId} isFrozen={isFrozen} onRecalculate={() => setRecalcTarget(output)} />
          ))
        )}
      </SectionCard>

      {(showAddOutput || recalcTarget) && (
        <AddOutputModal
          scopeId={scopeId}
          linkedItems={linkedItems}
          superseding={recalcTarget}
          onClose={() => { setShowAddOutput(false); setRecalcTarget(null) }}
          onSaved={async () => { setShowAddOutput(false); setRecalcTarget(null); onSuccess('Resource quantity output saved.'); await load() }}
          onError={onError}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------
// A single Output row, with per-output Geometry Change detection: each
// item in geometry_snapshot.items is compared against its LIVE aggregated
// quantity — never against the Scope's geometry as a whole, since
// different Outputs may use different subsets.
// -----------------------------------------------------------------------
function OutputRow({ output, scopeId, isFrozen, onRecalculate }) {
  const [liveValues, setLiveValues] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [previousVersions, setPreviousVersions] = useState(null)
  const [expandedVersionId, setExpandedVersionId] = useState(null)
  const [showRawJson, setShowRawJson] = useState(false)

  useEffect(() => {
    const items = output.geometry_snapshot?.items || []
    if (items.length === 0) { setLiveValues({}); return }
    Promise.all(
      items.map((it) =>
        supabase.from('takeoff_item_aggregated_quantity').select('aggregated_quantity').eq('takeoff_item_id', it.takeoff_item_id).maybeSingle()
      )
    ).then((results) => {
      const map = {}
      items.forEach((it, i) => { map[it.takeoff_item_id] = results[i].data?.aggregated_quantity ?? null })
      setLiveValues(map)
    })
  }, [output])

  // Previous versions of this SAME logical output are identified by
  // sharing the same scope_id + module_key while being inactive — the
  // existing data model already carries everything needed (is_active,
  // created_at); no new table or column is required for History.
  useEffect(() => {
    supabase
      .from('resource_quantity_output')
      .select('*')
      .eq('scope_id', scopeId)
      .eq('module_key', output.module_key)
      .eq('is_active', false)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => { if (!error) setPreviousVersions(data || []) })
  }, [scopeId, output.module_key, output.id])

  const items = output.geometry_snapshot?.items || []
  const changedItems = items.filter((it) => liveValues && liveValues[it.takeoff_item_id] != null && Number(liveValues[it.takeoff_item_id]) !== Number(it.value))
  const hasChanged = changedItems.length > 0
  const historyCount = previousVersions?.length ?? 0

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>{output.module_key}</span>
          <span style={{ fontSize: 10, color: colors.textMuted, marginLeft: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>Resource Quantity</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: colors.accentGreen, marginLeft: 8, textTransform: 'uppercase', letterSpacing: 0.3 }}>● Active</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: colors.primary }}>{formatNumber(output.calculated_value)} {output.unit}</span>
          <button onClick={() => setExpanded((v) => !v)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 11 }}>{expanded ? 'Hide' : 'Details'}</button>
          {historyCount > 0 && (
            <button onClick={() => setHistoryOpen((v) => !v)} style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 4, color: colors.textSecondary, cursor: 'pointer', fontSize: 11, padding: '3px 8px' }}>
              {historyOpen ? 'Hide History' : `History (${historyCount})`}
            </button>
          )}
          {!isFrozen && <Button size="sm" variant="secondary" onClick={onRecalculate}>Recalculate</Button>}
        </div>
      </div>

      {hasChanged && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: colors.warningBg, borderRadius: 6, fontSize: 11, color: colors.warningText }}>
          ⚠ Geometry Changed — this quantity was calculated using an older geometry snapshot.
          {changedItems.map((it) => (
            <div key={it.takeoff_item_id} style={{ marginTop: 3 }}>
              {it.label}: saved <strong>{formatNumber(it.value)}</strong> {it.unit} → now <strong>{formatNumber(liveValues[it.takeoff_item_id])}</strong> {it.unit}
            </div>
          ))}
        </div>
      )}

      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}`, fontSize: 11, color: colors.textSecondary }}>
          {(() => {
            const moduleDef = MODULE_REGISTRY[output.module_key]
            if (typeof moduleDef?.renderBreakdown === 'function') {
              // Estimator-friendly view — built purely from already-stored
              // engineering_inputs/breakdown_snapshot, no recalculation.
              const breakdown = moduleDef.renderBreakdown(output.engineering_inputs, output.breakdown_snapshot)
              return (
                <>
                  <ModuleBreakdownRenderer breakdown={breakdown} />
                  <button onClick={() => setShowRawJson((v) => !v)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 10, marginTop: 6, textDecoration: 'underline' }}>
                    {showRawJson ? 'Hide' : 'Show'} raw technical data
                  </button>
                  {showRawJson && (
                    <pre style={{ margin: '6px 0 0', fontFamily: 'monospace', fontSize: 10, background: '#FAFBFC', padding: 8, borderRadius: 6 }}>
                      {JSON.stringify({ geometry_snapshot: output.geometry_snapshot, engineering_inputs: output.engineering_inputs, breakdown_snapshot: output.breakdown_snapshot }, null, 2)}
                    </pre>
                  )}
                </>
              )
            }
            // Generic fallback — unchanged behaviour for any module without
            // a custom renderBreakdown (e.g. the generic placeholder module).
            return (
              <>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Geometry Used</div>
                {items.map((it) => <div key={it.takeoff_item_id}>{it.label}: {formatNumber(it.value)} {it.unit}</div>)}
                <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Engineering Inputs</div>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 10 }}>{JSON.stringify(output.engineering_inputs, null, 2)}</pre>
                <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Calculation Steps</div>
                {(output.breakdown_snapshot?.steps || []).map((s, i) => <div key={i}>{s.label}: {formatNumber(s.value)} {s.unit}</div>)}
              </>
            )
          })()}
        </div>
      )}

      {/* ===================== HISTORY — strictly read-only ===================== */}
      {historyOpen && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>Previous Versions (read-only)</div>
          {previousVersions.map((v) => (
            <div key={v.id} style={{ background: '#FAFBFC', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 12 }}>
                  <strong style={{ color: colors.textPrimary }}>{formatNumber(v.calculated_value)} {v.unit}</strong>
                  <span style={{ color: colors.textMuted, marginLeft: 8, fontSize: 11 }}>
                    {new Date(v.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <button
                  onClick={() => setExpandedVersionId((prev) => (prev === v.id ? null : v.id))}
                  style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 11 }}
                >
                  {expandedVersionId === v.id ? 'Hide' : 'Details'}
                </button>
              </div>
              {expandedVersionId === v.id && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${colors.border}`, fontSize: 11, color: colors.textSecondary }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Module: {v.module_key}</div>
                  <div style={{ fontWeight: 700, marginTop: 6, marginBottom: 4 }}>Geometry Used</div>
                  {(v.geometry_snapshot?.items || []).map((it) => <div key={it.takeoff_item_id}>{it.label}: {formatNumber(it.value)} {it.unit}</div>)}
                  <div style={{ fontWeight: 700, marginTop: 6, marginBottom: 4 }}>Engineering Inputs</div>
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: 10 }}>{JSON.stringify(v.engineering_inputs, null, 2)}</pre>
                  <div style={{ fontWeight: 700, marginTop: 6, marginBottom: 4 }}>Calculation Steps</div>
                  {(v.breakdown_snapshot?.steps || []).map((s, i) => <div key={i}>{s.label}: {formatNumber(s.value)} {s.unit}</div>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SectionCard({ title, action, children, style }) {
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 14, ...style }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>{title}</span>
        {action}
      </div>
      {children}
    </div>
  )
}

// -----------------------------------------------------------------------
// AddOutputModal — Manual / Calculated tabs. Handles both first-time
// creation and Recalculation (via `superseding`).
// -----------------------------------------------------------------------
function AddOutputModal({ scopeId, linkedItems, superseding, onClose, onSaved, onError }) {
  // The default tab opens on the module that produced `superseding`
  // (Recalculate must land on the same module, not force the estimator
  // to re-pick it) — for a brand-new Output it defaults to Manual, since
  // the standalone test/proof-of-concept module is no longer offered as
  // a selectable starting point.
  const [tab, setTab] = useState(superseding?.module_key === 'rebar_slab' ? 'rebar_slab' : superseding?.module_key === 'rebar_beam' ? 'rebar_beam' : superseding?.module_key === 'rebar_column' ? 'rebar_column' : superseding?.module_key === 'rebar_foundation' ? 'rebar_foundation' : superseding?.module_key === 'rebar_wall' ? 'rebar_wall' : superseding?.module_key === 'generic_area_x_factor' ? 'calculated' : 'manual')
  const [manualValue, setManualValue] = useState('')
  const [manualUnit, setManualUnit] = useState('')
  const [selectedGeomIds, setSelectedGeomIds] = useState(new Set(superseding?.geometry_snapshot?.items?.map((i) => i.takeoff_item_id) || []))
  const [factor, setFactor] = useState(superseding?.engineering_inputs?.factor ?? '')
  const [geomLiveValues, setGeomLiveValues] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    Promise.all(linkedItems.map((it) => supabase.from('takeoff_item_aggregated_quantity').select('aggregated_quantity').eq('takeoff_item_id', it.id).maybeSingle()))
      .then((results) => {
        const map = {}
        linkedItems.forEach((it, i) => { map[it.id] = results[i].data?.aggregated_quantity ?? 0 })
        setGeomLiveValues(map)
      })
  }, [linkedItems])

  function toggleGeom(id) {
    setSelectedGeomIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedItems = linkedItems
    .filter((it) => selectedGeomIds.has(it.id))
    .map((it) => ({ takeoff_item_id: it.id, measurement_type: it.measurement_type, value: geomLiveValues[it.id] ?? 0, unit: it.unit, label: it.description, detail: null }))

  const calcResult = useMemo(() => {
    if (tab !== 'calculated') return null
    try {
      return { value: PLACEHOLDER_MODULE.calculate(selectedItems, { factor: factor === '' ? '' : Number(factor) }), error: null }
    } catch (e) {
      return { value: null, error: e.message }
    }
  }, [tab, selectedItems, factor])

  async function handleSave() {
    // HARD GUARD: this generic handler must be structurally impossible
    // to run for any dedicated engineering module (rebar_slab, rebar_beam,
    // and any future one) — MODULE_REGISTRY[tab]?.isDedicatedPanel is the
    // SAME single source of truth used to decide which UI renders for
    // this tab, so this guard can never drift out of sync with it. A
    // dedicated module saves exclusively through its own onSave(payload)
    // prop below; this function is reserved for 'manual' and the
    // retired generic placeholder path only.
    if (MODULE_REGISTRY[tab]?.isDedicatedPanel) {
      onError('Internal error: the generic save handler cannot be used for a dedicated engineering module. Use that module\'s own Save Output button.')
      return
    }
    setSaving(true)
    const { data: authData } = await supabase.auth.getUser()

    let payload
    if (tab === 'manual') {
      if (manualValue.trim() === '' || Number.isNaN(Number(manualValue)) || !manualUnit.trim()) {
        onError('Value and unit are required for a manual output.')
        setSaving(false)
        return
      }
      payload = {
        p_module_key: 'manual', p_calculated_value: Number(manualValue), p_unit: manualUnit.trim(),
        p_geometry_snapshot: { items: [] }, p_engineering_inputs: {}, p_breakdown_snapshot: {},
      }
    } else {
      if (!calcResult?.value) { onError(calcResult?.error || 'Cannot save — calculation is incomplete.'); setSaving(false); return }
      payload = {
        p_module_key: PLACEHOLDER_MODULE.moduleKey, p_calculated_value: calcResult.value.finalQuantity, p_unit: selectedItems[0]?.unit || '',
        p_geometry_snapshot: { items: selectedItems }, p_engineering_inputs: { factor: Number(factor) },
        p_breakdown_snapshot: { steps: calcResult.value.steps },
      }
    }

    const { error } = await supabase.rpc('record_resource_quantity_output', {
      p_scope_id: scopeId, ...payload, p_created_by: authData.user?.id,
      p_superseded_output_id: superseding?.id ?? null,
    })
    setSaving(false)
    if (error) onError(error.message)
    else onSaved()
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,16,23,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: '#fff', borderRadius: 10, width: tab === 'rebar_slab' ? 'min(720px, 95vw)' : 'min(520px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>{superseding ? 'Recalculate Output' : 'Add Output'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 18px', borderBottom: `1px solid ${colors.border}` }}>
          <button
            onClick={() => setTab('manual')}
            style={{
              padding: '8px 14px', borderRadius: 6, border: `1px solid ${colors.border}`, background: tab === 'manual' ? '#F0F4FF' : '#fff',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: tab === 'manual' ? colors.primary : colors.textSecondary,
            }}
          >
            Manual
          </button>
          {/* The generic proof-of-concept module is retained ONLY so a
              pre-existing Output created with it can still be viewed and
              recalculated — it never appears in the Module Picker as a
              starting option for a NEW Output. */}
          {superseding?.module_key === 'generic_area_x_factor' && (
            <button
              onClick={() => setTab('calculated')}
              style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${colors.border}`, background: tab === 'calculated' ? '#F0F4FF' : '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: tab === 'calculated' ? colors.primary : colors.textSecondary }}
            >
              Generic Module
            </button>
          )}
          <ModulePickerPopover
            selectedKey={MODULE_REGISTRY[tab]?.isDedicatedPanel ? tab : null}
            onSelect={(moduleKey) => setTab(moduleKey)}
          />
        </div>

        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {MODULE_REGISTRY[tab]?.isDedicatedPanel ? (
            (() => {
              // Both dedicated Rebar panels share the exact same
              // persistence adapter — only the moduleKey differs. There
              // is no per-module duplication of the RPC call itself.
              const savePayloadAsOutput = async (moduleKey, payload) => {
                setSaving(true)
                const { data: authData } = await supabase.auth.getUser()
                const { error } = await supabase.rpc('record_resource_quantity_output', {
                  p_scope_id: scopeId,
                  p_module_key: moduleKey,
                  p_calculated_value: payload.calculatedValueTon,
                  p_unit: payload.unit,
                  p_geometry_snapshot: payload.geometrySnapshot,
                  p_engineering_inputs: payload.engineeringInputs,
                  p_breakdown_snapshot: payload.breakdownSnapshot,
                  p_created_by: authData.user?.id,
                  p_superseded_output_id: superseding?.id ?? null,
                })
                setSaving(false)
                if (error) { onError(error.message); return false }
                onSaved()
                return true
              }
              return (
              <DedicatedModuleWrapper moduleKey={tab}>
                {(() => {
                  const Panel = DEDICATED_PANEL_COMPONENTS[tab]
                  if (!Panel) return <div style={{ fontSize: 12, color: colors.danger }}>Unknown module "{tab}".</div>
                  return (
                    <Panel
                      scopeId={scopeId}
                      linkedItems={linkedItems}
                      superseding={superseding?.module_key === tab ? superseding : null}
                      onClose={onClose}
                      onError={onError}
                      saving={saving}
                      onSave={(payload) => savePayloadAsOutput(tab, payload)}
                    />
                  )
                })()}
              </DedicatedModuleWrapper>
              )
            })()
          ) : tab === 'manual' ? (
            <>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Quantity — required</label>
              <Input type="number" step="any" value={manualValue} onChange={(e) => setManualValue(e.target.value)} style={{ width: '100%', marginBottom: 12 }} />
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 5 }}>Unit — required</label>
              <Input value={manualUnit} onChange={(e) => setManualUnit(e.target.value)} placeholder="e.g. ton, m2, no" style={{ width: '100%' }} />
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>Module</div>
              <div style={{ fontSize: 12, color: colors.textPrimary, marginBottom: 14 }}>{PLACEHOLDER_MODULE.label}</div>

              <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, marginBottom: 6 }}>Select Geometry</div>
              {linkedItems.length === 0 && <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>No geometry linked to this scope yet.</div>}
              {linkedItems.map((it) => (
                <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0' }}>
                  <input type="checkbox" checked={selectedGeomIds.has(it.id)} onChange={() => toggleGeom(it.id)} />
                  {it.description} ({formatNumber(geomLiveValues[it.id] ?? 0)} {it.unit})
                </label>
              ))}

              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: colors.textSecondary, margin: '14px 0 5px' }}>Factor — required</label>
              <Input type="number" step="any" value={factor} onChange={(e) => setFactor(e.target.value)} style={{ width: '100%' }} />

              {calcResult?.value && (
                <div style={{ marginTop: 14, padding: 10, background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
                  {calcResult.value.steps.map((s, i) => <div key={i} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}><span>{s.label}</span><strong>{formatNumber(s.value)} {s.unit}</strong></div>)}
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}`, fontSize: 18, fontWeight: 800, color: colors.primary }}>
                    {formatNumber(calcResult.value.finalQuantity)} {selectedItems[0]?.unit}
                  </div>
                </div>
              )}
              {calcResult?.error && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 10, fontStyle: 'italic' }}>{calcResult.error}</div>}
            </>
          )}
        </div>

        {!MODULE_REGISTRY[tab]?.isDedicatedPanel && (
          <div style={{ padding: '12px 18px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || (tab === 'calculated' && !calcResult?.value)}>
              {saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
